import { Box, Chip, Stack } from "@mui/material";
import { isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AppIconButton from "@/common/components/Button/AppIconButton";
import Datetime from "@/common/components/Datetime/Datetime";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import GeneralStageHistory from "@/common/components/GeneralStageHistory";
import AppIcon from "@/common/components/Icons/AppIcon";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import GeneralPriceSummaryDisplay from "@/common/components/PriceSummary/GeneralPriceSummaryDisplay";
import AppTypography from "@/common/components/Text/AppTypography";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { enumService } from "@/common/services/enum";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, EntityType, WheelOperationDto } from "@/core/api/generated/v1";

import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralByWhoDisplay from "../../../General/Display/GeneralByWhoDisplay";
import SupplierLink from "../../../Supplier/SupplierLink";
import VehicleLink from "../../../Vehicle/VehicleLink";

interface Props {
  wheelOperation: WheelOperationDto;
  onUpdate: (newValue: Nil<WheelOperationDto>) => void;
}

export default function OverviewTabContent({ wheelOperation, onUpdate }: Props) {
  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);
  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={wheelOperation} />}
          />
        }
      >
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(wheelOperation?.localNumber)}
            >
              {wheelOperation?.localNumber}
            </InfoTile> */}

            <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(wheelOperation?.vehicle)}>
              <VehicleLink entity={wheelOperation?.vehicle} />
            </InfoTile>

            {/* <InfoTile label='Contract' iconOf='contract' isEmpty={isNil(wheelOperation?.contract)}>
              <ContractLink entity={wheelOperation?.contract} />
            </InfoTile> */}

            <InfoTile label='Supplier' iconOf='supplier' isEmpty={isNil(wheelOperation?.supplier)}>
              <SupplierLink entity={wheelOperation?.supplier} />
            </InfoTile>

            <InfoTile
              label='External number'
              iconOf='externalNumber'
              isEmpty={!wheelOperation.externalNumber}
              endAdornment={
                <BaseEditExternalNumberModal
                  initialValues={{ externalNumber: wheelOperation.externalNumber }}
                  onConfirm={async (externalNumber) => {
                    const response =
                      await apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdExternalNumberPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          wheelOperationId: wheelOperation.id || "",
                          generalExternalNumberInputDto: { externalNumber },
                        },
                      );
                    onUpdate(response.data);
                  }}
                />
              }
            >
              {wheelOperation.externalNumber}
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Schedule type'
              iconOf='data'
              isEmpty={isNil(wheelOperation?.scheduleType)}
            >
              <InlineApiEnumValue
                type='WheelOperationScheduleType'
                value={wheelOperation.scheduleType}
              />
            </InfoTile>
            <InfoTile label='Scheduled At' iconOf='date' isEmpty={!wheelOperation.scheduledAt}>
              <Datetime datetime={wheelOperation?.scheduledAt} />
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Mileage'
              iconOf='gauge'
              isEmpty={!wheelOperation.mileage}
              endAdornment={
                <BaseEditMileageModal
                  vehicleId={wheelOperation.vehicle?.id}
                  mileage={wheelOperation.mileage}
                  trigger={
                    <AppIconButton
                      sx={{ p: "2px", ml: 0.5 }}
                      onClick={() => setIsUpdateMileageModalOpened(true)}
                    >
                      <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                    </AppIconButton>
                  }
                  triggerParams={{ isDisableDefaultAction: true }}
                  open={isUpdateMileageModalOpened}
                  onClose={() => setIsUpdateMileageModalOpened(false)}
                  onConfirm={async (mileage) => {
                    await apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdMileagePut(
                      {
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        wheelOperationId: wheelOperation.id || "",
                        updateVehicleMileageDto: {
                          vehicleId: wheelOperation.vehicle?.id,
                          mileage: mileage,
                        },
                      },
                    );
                  }}
                  onMileageChanged={() => onUpdate(null)}
                />
              }
            >
              {wheelOperation.mileage?.mileage} {wheelOperation.mileage?.unit}
            </InfoTile>

            <InfoTile label='Fuel level' iconOf='fuel' isEmpty={isNil(wheelOperation?.fuelLevel)}>
              {wheelOperation?.fuelLevel}
            </InfoTile>

            <InfoTile
              label='Responsible user'
              iconOf='inspector'
              isEmpty={isNil(wheelOperation?.responsibleUser)}
            >
              <GeneralByWhoDisplay who={wheelOperation.responsibleUser} />
            </InfoTile>

            <InfoTile
              label='Notes'
              iconOf='notes'
              isEmpty={!wheelOperation.notes}
              endAdornment={
                <BaseEditNotesModal
                  initialValues={{ notes: wheelOperation.notes }}
                  onConfirm={async (notes) => {
                    const response =
                      await apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdNotesPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          wheelOperationId: wheelOperation.id!,
                          generalNotesInputDto: { notes },
                        },
                      );
                    onUpdate(response.data);
                  }}
                />
              }
            >
              {wheelOperation.notes}
            </InfoTile>
          </Box>
        </ViewContentBlock>

        {/* Service Types */}
        <ViewContentBlock>
          <AppTypography variant='h3'>Service types</AppTypography>
          <Stack spacing={1} direction='row'>
            {wheelOperation?.serviceTypes?.map((serviceType) => (
              <Chip
                key={serviceType}
                label={enumService.getEnumValueName("WheelServiceType", serviceType)}
              />
            ))}
          </Stack>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.WheelOperation}
          entityId={wheelOperation.id}
          entity={wheelOperation}
          entityUpdatePermission={AppPermission.WheelOperationManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={() => onUpdate(null)}
        />

        {/* Stage */}
        <ViewContentBlock>
          <AppTypography variant='h3'>Stage</AppTypography>
          <GeneralStageHistory
            stageEnumType='WheelOperationStage'
            stageHistory={wheelOperation.stageHistory}
            withUpdateButtons
            onUpdateStage={async (newStage) => {
              const response =
                await apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdStagePut({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  wheelOperationId: wheelOperation.id || "",
                  generalStageUpdateDtoOfWheelOperationStage: {
                    newStage,
                  },
                });
              onUpdate && onUpdate(response.data);
            }}
          />
        </ViewContentBlock>

        {!ArrayHelper.isEmpty(wheelOperation.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(wheelOperation.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {/* Summary */}
        <ViewContentBlock direction='row' alignSelf='end' sx={{ width: "fit-content" }}>
          <GeneralPriceSummaryDisplay
            sx={{ minWidth: { xxs: "100%", md: "300px" } }}
            summary={{
              currency: wheelOperation.currency,
              subTotal: wheelOperation.subTotal,
              subTotalIncDiscount: wheelOperation.subTotalIncDiscount,
              discount: wheelOperation.discount,
              tax: wheelOperation.tax,
              total: wheelOperation.total,
            }}
          />
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
